import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import {
  ShoppingCart,
  Trash2,
  ArrowLeft,
  Loader2,
  MessageCircle,
  Share2,
  Store,
  Package,
} from "lucide-react";
import Header from "../../components/layout/header";
import Footer from "../../components/layout/footer";

export default function CartPage() {
  const [cart, setCart] = useState([]);
  const [stores, setStores] = useState({});
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [whatsappError, setWhatsappError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentStore, setCurrentStore] = useState(null);
  const router = useRouter();

  // Load cart from localStorage and group by store, and also reload on route changes
  useEffect(() => {
    const loadCart = () => {
      try {
        const cartData = JSON.parse(localStorage.getItem("cart") || "[]");
        const safeCart = Array.isArray(cartData) ? cartData : [];
        setCart(safeCart);

        const storeGroups = safeCart.reduce((groups, item) => {
          const { id: storeId, name: storeName, phone: whatsapp } =
            item.store || {};
          const key = storeId || "unknown";

          if (!groups[key]) {
            groups[key] = {
              id: key,
              storeName: storeName || "Unknown Store",
              whatsapp: whatsapp || "",
              items: [],
            };
          }
          groups[key].items.push(item);
          return groups;
        }, {});

        setStores(storeGroups);
      } catch (e) {
        console.error("Failed to load cart:", e);
        setCart([]);
        setStores({});
      }
    };

    loadCart();
    window.addEventListener("cartUpdated", loadCart);

    const handleRouteChange = (url) => {
      if (url === "/cart") loadCart();
    };
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      window.removeEventListener("cartUpdated", loadCart);
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  const formatWhatsappNumber = (num) => {
    const cleaned = String(num || "").replace(/\D/g, "");
    if (!cleaned) return "";
    if (cleaned.startsWith("62")) return cleaned;
    if (cleaned.startsWith("0")) return "62" + cleaned.substring(1);
    return cleaned;
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    const updatedCart = cart.map((item) =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    setCart(updatedCart);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const removeItem = (id) => {
    const updatedCart = cart.filter((item) => item.id !== id);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    setCart(updatedCart);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const showOrderConfirmation = (store) => {
    setCurrentStore(store);
    setShowConfirmation(true);
  };

  const handleStoreCheckout = (store) => {
    setShowConfirmation(false);
    setLoading(true);
    setWhatsappError(null);

    const orderItems = store.items
      .map(
        (item) =>
          `➤ ${item.name}\n  Jumlah: ${item.quantity}\n  Harga: Rp${item.price.toLocaleString(
            "id-ID"
          )}\n  Subtotal: Rp${(item.price * item.quantity).toLocaleString(
            "id-ID"
          )}`
      )
      .join("\n\n");

    const subtotal = store.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const now = new Date();
    const orderDate = now.toLocaleDateString("id-ID", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const orderTime = now.toLocaleTimeString("id-ID");

    const message = `Halo ${
      store.storeName
    },\n\nSaya ingin memesan produk berikut:\n\n${orderItems}\n\n────────────────\n💳 *Total Pesanan*: Rp${subtotal.toLocaleString(
      "id-ID"
    )}\n📅 *Tanggal Pesan*: ${orderDate} ${orderTime}\n\n🚚 *Info Pengiriman*:\nNama: [Nama Lengkap]\nAlamat: [Alamat Lengkap]\nNo. Whatsapp: [Nomor WA]\n\nHarga diatas belum termasuk ongkos kirim. Mohon Menunggu, akan dihubungi untuk proses lebih lanjut. Terima kasih.`;

    const cleanWhatsapp = formatWhatsappNumber(store.whatsapp);

    if (cleanWhatsapp) {
      const whatsappUrl = `https://wa.me/${cleanWhatsapp}?text=${encodeURIComponent(
        message
      )}`;

      if (
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        window.location.href = whatsappUrl;
        setTimeout(() => window.open(whatsappUrl, "_blank"), 500);
      } else {
        window.open(whatsappUrl, "_blank");
      }

      // Remove items from this store only
      const updatedCart = cart.filter(
        (item) => (item.store?.id || "unknown") !== store.id
      );
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      setCart(updatedCart);
      window.dispatchEvent(new Event("cartUpdated"));
    } else {
      setWhatsappError(
        `Toko ${store.storeName} belum memiliki nomor WhatsApp yang valid`
      );
    }

    setLoading(false);
  };

  const shareCart = async () => {
    try {
      const cartData = encodeURIComponent(JSON.stringify(cart));
      const shareUrl = `${window.location.origin}${router.pathname}?cart=${cartData}`;

      if (navigator.share) {
        await navigator.share({
          title: "Keranjang Belanja Saya",
          text: "Lihat keranjang belanja saya",
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link keranjang telah disalin ke clipboard!");
      }
    } catch (e) {
      console.error("Sharing failed:", e);
    }
  };

  const overallSubtotal = useMemo(
    () =>
      cart.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0),
    [cart]
  );
  // const tax = overallSubtotal * 0.1;
  const shipping = overallSubtotal > 100 ? 0 : 10;
  const total = overallSubtotal + shipping;

  const storeEntries = Object.entries(stores);

  return (
    <div className={darkMode ? "dark" : ""}>
      <Header cartCount={cart.length} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 max-w-md w-full shadow-lg">
              <h3 className="text-lg font-bold mb-2">Konfirmasi Pesanan</h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-6">
                Apakah Anda yakin ingin memesan dari{" "}
                <span className="font-semibold">{currentStore?.storeName}</span>?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg"
                >
                  Batal
                </button>
                <button
                  onClick={() => handleStoreCheckout(currentStore)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin w-5 h-5" /> : null}
                  Ya, Pesan Sekarang
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => router.back()}
            className="flex items-center text-sm text-[#003B5C] dark:text-[#00778B] mr-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center">
            <ShoppingCart className="w-6 h-6 mr-2" />
            Your Cart ({cart.length})
          </h1>
        </div>

        {whatsappError && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <p>{whatsappError}</p>
          </div>
        )}

        {/* Empty State */}
        {cart.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm p-8 md:p-12 text-center">
            <div className="max-w-md mx-auto">
              <ShoppingCart className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
              <h3 className="text-xl font-medium mb-2">Your cart is empty</h3>
              <p className="text-zinc-500 mb-6">
                Looks like you haven't added any items to your cart yet
              </p>
              <Link
                href="/f&b"
                className="px-6 py-3 bg-[#003B5C] hover:bg-[#015c87] text-white rounded-lg font-medium transition-colors inline-block"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Store Cards (model seperti foto) */}
            {storeEntries.map(([storeId, store]) => {
              const storeSubtotal = store.items.reduce(
                (sum, item) => sum + item.price * (item.quantity || 1),
                0
              );
              const itemCount = store.items.reduce(
                (sum, item) => sum + (item.quantity || 1),
                0
              );

              return (
                <div
                  key={storeId}
                  className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm overflow-hidden border border-zinc-200 dark:border-zinc-800"
                >
                  <div className="grid md:grid-cols-[280px_1fr]">
                    {/* LEFT: Summary (seperti sidebar di gambar) */}
                    <div className="p-5 md:border-r border-zinc-200 dark:border-zinc-800">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400">
                            Store
                          </div>
                          <div className="font-bold text-lg flex items-center gap-2">
                            <Store className="w-5 h-5" />
                            {store.storeName}
                          </div>

                          {store.whatsapp ? (
                            <div className="text-sm text-zinc-500 mt-1">
                              WhatsApp: {store.whatsapp}
                            </div>
                          ) : (
                            <div className="text-sm text-red-500 mt-1">
                              WhatsApp belum tersedia
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-5 space-y-3 text-sm">
                        <div className="flex items-center justify-between text-zinc-600 dark:text-zinc-300">
                          <span className="inline-flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            Items
                          </span>
                          <span className="font-semibold">{itemCount}</span>
                        </div>

                        <div className="flex items-center justify-between text-zinc-600 dark:text-zinc-300">
                          <span>Total Amount</span>
                          <span className="font-bold text-zinc-900 dark:text-white">
                            Rp{storeSubtotal.toLocaleString("id-ID")}
                          </span>
                        </div>
                      </div>

                      <div className="mt-6 flex flex-col gap-3">
                        <button
                          onClick={() =>
                            showOrderConfirmation({ ...store, id: storeId })
                          }
                          disabled={loading || !store.whatsapp}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
                        >
                          {loading ? (
                            <Loader2 className="animate-spin w-4 h-4" />
                          ) : (
                            <MessageCircle className="w-4 h-4" />
                          )}
                          Order via WhatsApp
                        </button>
                      </div>
                    </div>

                    {/* RIGHT: Items list (seperti daftar product di gambar) */}
                    <div className="p-2 sm:p-4">
                      <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
                        {store.items.map((item) => (
                          <div
                            key={item.id}
                            className="p-3 sm:p-4 flex items-center gap-4"
                          >
                            {/* Thumb */}
                            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800 flex-shrink-0">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-400">
                                  <ShoppingCart className="w-7 h-7" />
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <h3 className="font-semibold text-zinc-900 dark:text-white truncate">
                                    {item.name}
                                  </h3>
                                  <p className="text-sm text-zinc-500 mt-0.5">
                                    Rp{item.price.toLocaleString("id-ID")}
                                  </p>
                                </div>

                                {/* Price on right (mirip gambar) */}
                                <div className="text-right">
                                  <div className="font-bold text-zinc-900 dark:text-white">
                                    Rp{(item.price * item.quantity).toLocaleString("id-ID")}
                                  </div>
                                  <button
                                    onClick={() => removeItem(item.id)}
                                    className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                                    aria-label={`Remove ${item.name}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Remove
                                  </button>
                                </div>
                              </div>

                              {/* Qty controls */}
                              <div className="mt-3 inline-flex items-center rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700">
                                <button
                                  onClick={() =>
                                    updateQuantity(item.id, item.quantity - 1)
                                  }
                                  className="px-3 py-1.5 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 disabled:opacity-50"
                                  disabled={item.quantity <= 1}
                                  aria-label={`Decrease quantity of ${item.name}`}
                                >
                                  -
                                </button>
                                <span className="px-4 py-1.5 bg-white dark:bg-zinc-900 text-sm font-medium">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    updateQuantity(item.id, item.quantity + 1)
                                  }
                                  className="px-3 py-1.5 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700"
                                  aria-label={`Increase quantity of ${item.name}`}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Right footer subtotal (opsional) */}
                      <div className="px-3 sm:px-4 pb-4 pt-3 flex justify-end">
                        <div className="text-sm text-zinc-600 dark:text-zinc-300">
                          Subtotal Store:{" "}
                          <span className="font-bold text-zinc-900 dark:text-white">
                            Rp{storeSubtotal.toLocaleString("id-ID")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Order Summary (overall) */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm p-6 border border-zinc-200 dark:border-zinc-800">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Order Summary</h2>
                <button
                  onClick={shareCart}
                  className="flex items-center gap-1 text-sm text-[#003B5C] dark:text-[#00778B]"
                >
                  <Share2 className="w-4 h-4" />
                  Share Cart
                </button>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-zinc-600 dark:text-zinc-400">
                    Subtotal
                  </span>
                  <span className="font-medium">
                    Rp{overallSubtotal.toLocaleString("id-ID")}
                  </span>
                </div>
                {/* <div className="flex justify-between">
                  <span className="text-zinc-600 dark:text-zinc-400">
                    Tax (10%)
                  </span>
                  <span className="font-medium">
                    Rp{tax.toLocaleString("id-ID")}
                  </span>
                </div> */}
                <div className="flex justify-between">
                  <span className="text-zinc-600 dark:text-zinc-400">
                    Shipping
                  </span>
                  <span className="font-medium">
                    {shipping === 0
                      ? "Free"
                      : `Rp${shipping.toLocaleString("id-ID")}`}
                  </span>
                </div>
              </div>

              <div className="border-t border-zinc-200 dark:border-zinc-700 pt-4 mb-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>Rp{total.toLocaleString("id-ID")}</span>
                </div>
              </div>

              {/* <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-4 text-center">
                <Link
                  href="/product"
                  className="text-[#003B5C] dark:text-[#00778B] hover:underline"
                >
                  Continue Shopping
                </Link>
              </p> */}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
